import React from "react";
import { useTranslation } from "react-i18next";

import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { PackageCredit } from "app/utils/creditBundles";
import { cls } from "app/utils";

import styles from "./SessionCard.module.scss";

interface Props {
  rate: LessonRateModel;
  description?: string;
  currencySymbol?: string;
  /** Set when a package the athlete holds covers this option. */
  packageCredit?: PackageCredit;
  /** True when the option can only be booked with a package they lack. */
  requiresPackage: boolean;
  /** Next bookable time, when the payload carries one. */
  nextAvailable?: string;
  onBook: (rate: LessonRateModel) => void;
  onSeePackages: () => void;
}

/**
 * One Private Session card (frame 528:65540 and the Coach Profile frames). The
 * footer has three states, which is comment #18's ask:
 *
 *   price          → "Book Now"
 *   held package   → "Package: N sessions remaining" + "Book with Package"
 *   package-only   → "Requires Subscription" + "See Packages"
 */
const SessionCard: React.FC<Props> = ({
  rate,
  description,
  currencySymbol = "$",
  packageCredit,
  requiresPackage,
  nextAvailable,
  onBook,
  onSeePackages,
}) => {
  const { t } = useTranslation();

  // CN-2711: with the price hidden the footer holds only Book Now, so the grid
  // drops its first column rather than leaving the button in it.
  const hidePrice = !packageCredit && !requiresPackage && !!rate.hidePrice;

  return (
    // The testids are what the e2e flow steps through this page by: the coach page
    // is where an athlete picks their option now, and a hashed module class is no
    // handle for a spec to hold.
    <div className={styles.card} data-testid="coach-session-card">
      <div className={styles.head}>
        <div className={styles.duration}>
          <span className={styles.duration_value}>{rate.durationInMinutes}</span>
          <span className={styles.duration_unit}>{t("MIN")}</span>
        </div>
        <div className={styles.head_text}>
          {nextAvailable && (
            <div className={styles.next_available}>{nextAvailable}</div>
          )}
          <div className={styles.title} data-testid="coach-session-title">
            {rate.title}
          </div>
          {description && <div className={styles.description}>{description}</div>}
        </div>
      </div>

      <div className={cls(styles.footer, { [styles.footer_no_price]: hidePrice })}>
        {packageCredit ? (
          <>
            <span className={styles.package_note}>
              <i className="ico ico-star" />
              <span>
                {packageCredit.unlimited
                  ? t("Package: unlimited sessions")
                  : t("Package: {{count}} sessions remaining", {
                      count: packageCredit.remainingSessions,
                    })}
              </span>
            </span>
            <button
              type="button"
              className={styles.book_button}
              onClick={() => onBook(rate)}
              data-testid="session-book-with-package"
            >
              {t("Book with Package")}
            </button>
          </>
        ) : requiresPackage ? (
          <>
            <span className={styles.subscription_note}>
              <i className="ico ico-repeat" />
              <span>{t("Requires Subscription")}</span>
            </span>
            <button
              type="button"
              className={styles.packages_button}
              onClick={onSeePackages}
              data-testid="session-see-packages"
            >
              {t("See Packages")}
            </button>
          </>
        ) : (
          <>
            {!hidePrice && (
              <span className={styles.price}>
                {currencySymbol}
                {rate.rate}
              </span>
            )}
            <button
              type="button"
              className={styles.book_button}
              onClick={() => onBook(rate)}
              data-testid="session-book-now"
            >
              {t("Book Now")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
